import { useMemo } from 'react';

function useOrders({ orderProducts, productCatalogWithPayload, setOrderProducts, setOrderFormErrors, setOrderForm, getTodayDateKey }) {
  const orderProductSummaries = useMemo(() => {
    return orderProducts.map((entry) => {
      const selected = productCatalogWithPayload.find((product) => String(product.id) === String(entry.product_id));
      const fields = selected?.parsedPayload?.fields || {};
      const rawMaterialsList = Array.isArray(fields.raw_materials) ? fields.raw_materials : [];
      const multiplier = Math.max(1, parseInt(entry.quantity || 1, 10));
      const expandedRawMaterials = rawMaterialsList.map((item, index) => {
        const baseQty = parseFloat(item?.required_qty || 0);
        return {
          key: `${item?.raw_material_name || item?.type || 'raw'}-${index}`,
          raw_material_name: item?.raw_material_name || item?.type || 'Raw material',
          characteristics: item?.characteristics && typeof item.characteristics === 'object'
            ? item.characteristics
            : (item?.category ? { Type: item.category } : {}),
          perProductQty: Number.isFinite(baseQty) ? baseQty : 0,
          totalQty: Number.isFinite(baseQty) ? baseQty * multiplier : 0
        };
      });
      return {
        selected,
        rawMaterials: expandedRawMaterials,
        quantity: multiplier
      };
    });
  }, [orderProducts, productCatalogWithPayload]);

  const orderDraftTotals = useMemo(() => {
    const totalUnits = orderProductSummaries.reduce((sum, summary) => sum + (Number(summary.quantity) || 0), 0);
    const totalRequired = orderProductSummaries.reduce(
      (sum, summary) => sum + (summary.rawMaterials || []).reduce((inner, raw) => inner + (Number(raw.totalQty) || 0), 0),
      0
    );
    return { totalUnits, totalRequired };
  }, [orderProductSummaries]);

  const clearOrderFormErrors = () => {
    setOrderFormErrors((prev) => {
      const next = { ...prev };
      delete next.products;
      delete next.bom;
      return next;
    });
  };

  const handleAddOrderProductCard = () => {
    setOrderProducts((prev) => [...prev, { product_id: '', quantity: 1 }]);
    clearOrderFormErrors();
  };

  const handleRemoveOrderProductCard = (index) => {
    setOrderProducts((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
    clearOrderFormErrors();
  };

  const updateOrderProduct = (index, updates) => {
    setOrderProducts((prev) => prev.map((entry, idx) => (idx === index ? { ...entry, ...updates } : entry)));
    clearOrderFormErrors();
  };

  const resetOrderForm = () => {
    setOrderProducts([{ product_id: '', quantity: 1 }]);
    setOrderFormErrors({});
    setOrderForm((prev) => ({
      ...prev,
      buyer_name: '',
      delivery_date: getTodayDateKey()
    }));
  };

  return {
    handleAddOrderProductCard,
    handleRemoveOrderProductCard,
    orderDraftTotals,
    orderProductSummaries,
    resetOrderForm,
    updateOrderProduct
  };
}

export default useOrders;
